import { Inventory, InventoryListItem } from '@cibo/core'
import AddIcon from '@mui/icons-material/Add'
// import FilterList from '@mui/icons-material/FilterList'
// import Search from '@mui/icons-material/Search'
import CheckIcon from '@mui/icons-material/Check'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import HourglassTopIcon from '@mui/icons-material/HourglassTop'
import { Box, Button, Paper, Stack, Typography } from '@mui/material'
import {
  DataGridPro,
  GridColumns,
  GridRowParams,
  GridSortModel,
  GridValueGetterParams,
} from '@mui/x-data-grid-pro'
import { intuitiveFromNow } from 'consts/Theme'
import { useInventories } from 'features/Inventories'
import { SCOPE_THREE_ROUTES } from 'navigation/types/scopeThreeRoutes'
import numeral from 'numeral'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RRLink, generatePath, useNavigate } from 'react-router-dom'
import { useRowActionsColumn } from './useRowActionsColumn'

const thousand = 1000

export const InventoriesList = () => {
  const { t } = useTranslation('impact/InventoriesList')
  const navigate = useNavigate()
  const { data: inventories, isLoading, dataUpdatedAt } = useInventories()
  const rowActionsColumn = useRowActionsColumn()
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'modifiedDate', sort: 'desc' },
  ])

  const columns = useMemo(
    () => [
      rowActionsColumn,
      {
        field: 'name',
        headerName: t('name'),
        flex: 1,
      },
      {
        field: 'inventoryBoundaries',
        headerName: t('boundaryArea'),
        flex: 1,
        renderCell: ({ value }: { value: string }) => value,
        valueGetter: ({ row }: GridValueGetterParams<number, Inventory>) => {
          if (!!row.data.tillableAcreage) {
            return numeral(row.data.tillableAcreage / thousand).format('0,0')
          }
          if (row.status !== 'open') {
            // TODO: need to use actual value, which doesn't appear to exist on list return
            return t('awaitingAcreageValue')
          }
          return t('notLocked')
        },
      },
      {
        field: 'basePeriodCarbonIntensity',
        headerName: t('baseCarbonIntensity'),
        flex: 1,
        renderCell: ({ row }: GridValueGetterParams<number, Inventory>) => {
          const { icon, rowStatus, color } =
            row.data?.years?.length && row.data.years.length > 0
              ? {
                  icon: <CheckIcon />,
                  rowStatus: 'viewReport',
                  color: 'success.main',
                }
              : row.status === 'locked' && row.data?.ciJobResult?.status === 'running'
              ? {
                  icon: <HourglassTopIcon />,
                  rowStatus: 'calculating',
                  color: 'text.secondary',
                }
              : row.status !== 'open' && (!row.data?.years?.length || row.data.years.length < 0)
              ? {
                  icon: <FormatListBulletedIcon />,
                  rowStatus: 'basePeriodUnset',
                  color: 'info.main',
                }
              : {
                  icon: <FormatListBulletedIcon />,
                  rowStatus: 'boundariesUnset',
                  color: 'info.main',
                }

          return !!row.data.baselineCI ? (
            numeral(row.data.baselineCI).format('0.00')
          ) : (
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  height: 24,
                  width: 24,
                  color,
                }}
              >
                {icon}
              </Box>
              <Typography variant="body2">{t(rowStatus)}</Typography>
            </Stack>
          )
        },
      },
      {
        field: 'modifiedDate',
        headerName: t('lastUpdated'),
        flex: 0.5,
        renderCell: ({ value }: { value: string }) => intuitiveFromNow(value),
      },
    ],
    [dataUpdatedAt]
  )

  return (
    <Paper
      sx={{
        borderRadius: '12px',
        height: 'calc(100% - 24px)',
        m: 3,
        px: 0.5,
        paddingBlockStart: 3,
        paddingBlockEnd: 5,
      }}
      variant="outlined"
    >
      <Stack justifyContent="space-between" direction="row" px={3} spacing={1.5}>
        <Typography variant="h6">{t('inventories')}</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          {/* hiding these icons because they're nonfunctional at present */}
          {/* <Search />
          <FilterList /> */}
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            component={RRLink}
            to={SCOPE_THREE_ROUTES.CREATE}
          >
            {t('cta')}
          </Button>
        </Stack>
      </Stack>
      <DataGridPro<InventoryListItem>
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        rows={inventories?.items || []}
        getRowId={i => i.inventoryId}
        loading={isLoading}
        columns={columns as GridColumns<InventoryListItem>}
        onRowClick={(row: GridRowParams<InventoryListItem>) =>
          navigate(
            generatePath(SCOPE_THREE_ROUTES.DETAIL, {
              inventoryId: row.id.toString(),
            })
          )
        }
      />
    </Paper>
  )
}
